// DAS-3292: the account portfolio table says which columns look back and which look ahead.
//
// "Since you bought" groups Perf and Risk (what the holding did since you bought it);
// "Next quarter (forecast)" groups Return Prob, Exp Quarter Return and Reward ÷ Risk.
// A fund has no company figures, so the forecast model gives every fund the same pooled
// fill-in value (49.3% / -0.4%): on fund rows those cells read "no forecast for funds" and
// sort last. A fund row is recognised by its asset type (the same list as the advice's
// single-company rule, portfolio_advice_record.NON_SINGLE_NAME_ASSETTYPES, without cash);
// a row without an asset type falls back to the DAS-3253 test, fundamental_trend_label
// 'INSUFFICIENT_DATA'.
import React from 'react';
import { Tooltip } from 'antd';

export const BACKWARD_KEYS = ['performance', 'risk_rank'];
export const FORECAST_KEYS = ['return_prob', 'expected_q', 'risk_adj_q'];
export const FORECAST_NOTE = 'Forecast from company figures. Tested on past data (DAS-3145): no proven edge yet.';

export const FUND_TYPES = ['etf', 'etc', 'etn', 'fund', 'equity fund', 'mutual fund', 'bond'];

export const isFundRow = (record) => {
  if (!record) return false;
  const type = String(record.AssetType || '').trim().toLowerCase();
  if (type) return FUND_TYPES.includes(type);
  return record.fundamental_trend_label === 'INSUFFICIENT_DATA';
};

const noForecastCell = () => (
  <span data-testid="no-fund-forecast" style={{ color: '#999', fontSize: '11px' }}>no forecast for funds</span>
);

const forFunds = (col) => ({
  ...col,
  render: (value, record, index) => (isFundRow(record) ? noForecastCell() : col.render(value, record, index)),
  // fund rows last in both directions (antd passes the sort order as the third argument)
  sorter: col.sorter
    ? (a, b, order) => {
        const fa = isFundRow(a), fb = isFundRow(b);
        if (fa !== fb) return (fa ? 1 : -1) * (order === 'descend' ? -1 : 1);
        return col.sorter(a, b, order);
      }
    : col.sorter,
});

const heading = (text, testId, note) => (
  note ? (
    <Tooltip title={note} overlayStyle={{ maxWidth: 320 }}>
      <span data-testid={testId} style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>{text}</span>
    </Tooltip>
  ) : <span data-testid={testId}>{text}</span>
);

/** Replace each run of grouped keys by one group column; other columns stay as they are. */
export function groupAccountColumns(columns) {
  const out = [];
  let i = 0;
  while (i < columns.length) {
    const key = columns[i] && columns[i].key;
    const set = BACKWARD_KEYS.includes(key) ? BACKWARD_KEYS : FORECAST_KEYS.includes(key) ? FORECAST_KEYS : null;
    if (!set) {
      out.push(columns[i]);
      i += 1;
      continue;
    }
    const children = [];
    while (i < columns.length && set.includes(columns[i].key)) {
      children.push(set === FORECAST_KEYS ? forFunds(columns[i]) : columns[i]);
      i += 1;
    }
    out.push(set === FORECAST_KEYS
      ? { key: 'forecast_group', title: heading('Next quarter (forecast)', 'forecast-group-heading', FORECAST_NOTE), children }
      : { key: 'backward_group', title: heading('Since you bought', 'backward-group-heading'), children });
  }
  return out;
}
